import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import DataTable from "react-data-table-component";
import { API_BASE_URL } from "../../config";
import { ToastContainer, toast } from "react-toastify";

const TicketView = () => {
  const { id } = useParams();

  /* ---------------- STATES ---------------- */
  const [ticket, setTicket] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    work_title: "",
    work_start_date: "",
    work_end_date: "",
    work_cost: "",
    work_summary: "",
    work_doc: null,
  });

  /* ---------------- AUTH ---------------- */
  const getAuthHeader = () => {
    const token = localStorage.getItem("authToken");
    return { Authorization: `Bearer ${token}` };
  };

  /* ---------------- LOAD DATA ---------------- */
  const loadTicketInfo = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/tickets/${id}/details`,
        { headers: getAuthHeader() }
      );
      setTicket(res.data.ticket);
    } catch (err) {
      console.error(err);
    }
  };

  const loadWorkOrders = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/tickets/${id}/workorder`,
        { headers: getAuthHeader() }
      );
      setWorkOrders(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setWorkOrders([]);
    }
  };

  useEffect(() => {
    loadTicketInfo();
    loadWorkOrders();
  }, [id]);

  /* ---------------- FORM HANDLERS ---------------- */
  const handleInput = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleFile = (e) =>
    setForm({ ...form, work_doc: e.target.files[0] });

  /* ---------------- SUBMIT ---------------- */
  const submitWorkOrder = async (e) => {
    e.preventDefault();
    setSaving(true);

    const fd = new FormData();
    fd.append("asset_id", ticket.asset_id);
    fd.append("work_title", form.work_title);
    fd.append("work_start_date", form.work_start_date);
    fd.append("work_end_date", form.work_end_date);
    fd.append("work_cost", form.work_cost);
    fd.append("work_summary", form.work_summary);
    if (form.work_doc) fd.append("work_doc", form.work_doc);

    try {
      await axios.post(
        `${API_BASE_URL}/tickets/${id}/workorder`,
        fd,
        {
          headers: {
            ...getAuthHeader(),
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success("Work Order Added");
      setForm({
        work_title: "",
        work_start_date: "",
        work_end_date: "",
        work_cost: "",
        work_summary: "",
        work_doc: null,
      });

      loadWorkOrders();
    } catch (err) {
  console.error(err);

  // ✅ Laravel validation errors (422)
  if (err.response && err.response.status === 422) {
    const errors = err.response.data.errors;

    Object.values(errors).forEach((msgArr) => {
      toast.error(msgArr[0]); // show each validation message
    });

    return;
  }

  // ✅ Laravel message
  if (err.response?.data?.message) {
    toast.error(err.response.data.message);
    return;
  }

  // ✅ Fallback
  toast.error("Failed to save work order");
} finally {
  setSaving(false);
}

  };

  /* ---------------- FILTER ---------------- */
  const filteredWorkOrders = workOrders.filter((row) =>
    Object.values(row).some((v) =>
      String(v).toLowerCase().includes(search.toLowerCase())
    )
  );

  /* ---------------- TABLE ---------------- */
  const columns = [
    { name: "Asset Code", selector: (r) => r.asset_code },
    { name: "Asset Name", selector: (r) => r.asset_name },
    { name: "Order Title", selector: (r) => r.order_title },
    { name: "Cost", selector: (r) => r.work_cost },
    {
      name: "Date",
      selector: (r) =>
        r.created_on
          ? new Date(r.created_on).toLocaleDateString("en-GB")
          : "-",
    },
    {
      name: "Document",
      cell: (r) =>
        r.work_doc ? (
          <a
            href={r.document_url}
            target="_blank"
            rel="noreferrer"
            className="btn btn-sm btn-info"
          >
            View
          </a>
        ) : (
          "-"
        ),
    },
  ];

  if (!ticket) return <div className="p-4">Loading...</div>;

  return (
<div className="card p-1 ms-0">
      <ToastContainer position="bottom-right" />

      {/* ---------------- WORK ORDER FORM ---------------- */}
      <div className="card mb-4">
        <h5 className="mb-3">Add Work Order</h5>

        <form onSubmit={submitWorkOrder}>
          <div className="mb-3">
            <label className="form-label">Work Title</label>
            <input
              name="work_title"
              className="form-control required-input"
              value={form.work_title}
              onChange={handleInput}
            />
          </div>

         {/* START DATE */}
<div className="mb-3">
  <label className="form-label">Start Date</label>
  <input
    type="date"
    name="work_start_date"
    className="form-control required-input"
    value={form.work_start_date}
    min={new Date().toISOString().split("T")[0]}   // 🚫 past dates blocked
    onChange={handleInput}
  />
</div>

{/* END DATE */}
<div className="mb-3">
  <label className="form-label">End Date</label>
  <input
    type="date"
    name="work_end_date"
    className="form-control required-input"
    value={form.work_end_date}
    min={
      form.work_start_date
        ? form.work_start_date
        : new Date().toISOString().split("T")[0]
    }                                              // 🚫 before start date blocked
    onChange={handleInput}
  />
</div>


          <div className="mb-3">
            <label className="form-label">Cost</label>
            <input
              type="number"
              name="work_cost"
              min="0"
              className="form-control required-input"
              value={form.work_cost}
              onChange={(e) =>
                e.target.value >= 0 && handleInput(e)
              }
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Upload Document</label>
            <input type="file" className="form-control" onChange={handleFile} />
          </div>

          <div className="mb-4">
            <label className="form-label">Work Summary</label>
            <textarea
              rows="3"
              name="work_summary"
              className="form-control required-input"
              value={form.work_summary}
              onChange={handleInput}
            />
          </div>

          {/* <button className="btn btn-danger px-4">
            {saving ? "Saving..." : "Save"}
          </button> */}


          <button
  className="btn btn-primary"
>
  Save
</button>
        </form>
      </div>

      {/* ---------------- WORK ORDER LIST ---------------- */}
      <div className="card p-3">
        <h5 className="mb-2">Work Order List</h5>

        <div className="d-flex justify-content-end mb-2">
          <input
            type="text"
            placeholder="Search..."
            className="form-control form-control-sm"
            style={{ width: 200 }}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <DataTable
          columns={columns}
          data={filteredWorkOrders}
          pagination
          striped
          highlightOnHover
          responsive
        />
      </div>
    </div>
  );
};

export default TicketView;
